import { HomeCard } from "../home-card";
import { HomeChart } from "../home-chart";
import { VariationBadge } from "../variation-badge";
import styles from "./overview-view.module.scss";
import type { OverviewViewModel } from "./types-overview-view";

/** Cartão "Capex dos N Anos": total do período e a curva ano a ano (AC5). */
function CapexCard({ capex }: Pick<OverviewViewModel, "capex">) {
  return (
    <HomeCard
      title={capex.titulo}
      aside={
        <span className={styles.capexTotal}>
          Total: <strong>{capex.total}</strong>
        </span>
      }
      className={styles.capex}
      testId="home-card-capex"
    >
      <HomeChart
        points={capex.serie}
        ariaLabel={capex.ariaLabel}
        labelHeader="Ano"
        valueHeader="Capex"
        formatValue={capex.formatValue}
        formatPointLabel={capex.formatPointLabel}
        showPointLabels
        showAxisLabels
        baseline="relevo"
        suave
        className={styles.capexChart}
        testId="home-chart-capex"
      />
    </HomeCard>
  );
}

/** Cartão "Outlook de Capex de Obras": projeção do ano e contagem de Obras. */
function OutlookCard({ outlook }: Pick<OverviewViewModel, "outlook">) {
  return (
    <HomeCard title={outlook.titulo} className={styles.outlook} testId="home-card-outlook">
      <div className={styles.outlookHeadline}>
        <strong className={styles.outlookValue}>{outlook.valor}</strong>
        <VariationBadge
          value={outlook.variacao}
          suffix="vs orçamento"
          testId="home-outlook-variacao"
        />
      </div>

      {/* Empilhados no Desktop e lado a lado no responsivo, como no protótipo. */}
      <div className={styles.outlookTiles}>
        <div className={styles.tile}>
          <span className={styles.tileLabel}>Total de Obras</span>
          <strong className={styles.tileValue}>{outlook.totalObras}</strong>
        </div>

        <div className={styles.tile}>
          <span className={styles.tileLabel}>Obras Executadas</span>
          <strong className={styles.tileValue}>{outlook.obrasExecutadas}</strong>
        </div>
      </div>
    </HomeCard>
  );
}

/** Cartão "Total por Tipo de obra": um quadro por sigla, com valor e fatia. */
function TipoObraCard({ tipoObra }: Pick<OverviewViewModel, "tipoObra">) {
  return (
    <HomeCard title={tipoObra.titulo} className={styles.tipoObra} testId="home-card-tipo-obra">
      <ul className={styles.tipoObraGrid}>
        {tipoObra.itens.map((item) => (
          <li key={item.sigla} className={styles.tile} data-testid={item.testId}>
            <span className={styles.tileLabel}>{item.sigla}</span>
            <strong className={styles.tileValue}>{item.valor}</strong>
            <span className={styles.tileShare}>{item.percentual}</span>
          </li>
        ))}
      </ul>
    </HomeCard>
  );
}

/**
 * Serviços, Materiais e Facilidades — três dos sete blocos de indicadores da
 * Visão Geral (AC2), apresentados como faixas empilhadas no protótipo.
 */
function EscopoCards({ escopo }: Pick<OverviewViewModel, "escopo">) {
  return (
    <div className={styles.escopo}>
      {escopo.map((bloco) => (
        <div key={bloco.id} className={styles.escopoRow} data-testid={bloco.testId}>
          <span className={styles.escopoLabel}>{bloco.label}</span>
          <strong className={styles.escopoPercentual}>{bloco.percentual}</strong>

          <div className={styles.escopoMeta}>
            <span className={styles.escopoValor}>{bloco.valor}</span>
            <VariationBadge value={bloco.variacao} testId={bloco.variacaoTestId} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Cartão "Custos por Subsistema": ranking rolável de subsistemas. */
function CustosSubsistemaCard({ custos }: { custos: OverviewViewModel["custosPorSubsistema"] }) {
  return (
    <HomeCard
      title={
        <>
          Custos por Subsistema - <span className={styles.subsistemaAno}>{custos.ano}</span>
        </>
      }
      className={styles.subsistema}
      testId="home-card-custos-subsistema"
    >
      <ul className={styles.subsistemaList}>
        {custos.itens.map((item) => (
          <li key={item.codigo} className={styles.subsistemaRow}>
            <div className={styles.subsistemaIdentity}>
              <span className={styles.subsistemaNome}>{item.nome}</span>
              <span className={styles.subsistemaCodigo}>{item.codigo}</span>
            </div>
            <span className={styles.subsistemaValor}>{item.valor}</span>
          </li>
        ))}
      </ul>
    </HomeCard>
  );
}

/** Visão Geral: os 7 blocos de indicadores consolidados do portfólio (AC2). */
export function OverviewViewView({
  capex,
  outlook,
  tipoObra,
  escopo,
  custosPorSubsistema,
}: OverviewViewModel) {
  return (
    <div className={styles.grid} data-testid="home-overview">
      <CapexCard capex={capex} />
      <OutlookCard outlook={outlook} />
      <TipoObraCard tipoObra={tipoObra} />
      <EscopoCards escopo={escopo} />
      <CustosSubsistemaCard custos={custosPorSubsistema} />
    </div>
  );
}
